import React, { useEffect, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { motionSafe as motion } from "@/lib/motionSafe";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useLenis } from "lenis/react";
import { Menu, X } from "lucide-react";
import { useI18n } from "../../i18n";
import { BOOKING_URL } from "../../lib/booking";

export const Header = () => {
  const { lang, setLang, t } = useI18n();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const lenis = useLenis();
  const isHome = location.pathname === "/";
  // The services page exists as two language-specific URLs. On those routes
  // the language switcher must be a real crawlable link to the counterpart
  // URL (preserving any #service-anchor); everywhere else it stays a button.
  const servicesTwin =
    location.pathname === "/services"
      ? "/en/services"
      : location.pathname === "/en/services"
        ? "/services"
        : null;
  const servicesPath = location.pathname === "/en/services" ? "/en/services" : "/services";
  const isServicesRoute = location.pathname === "/services" || location.pathname === "/en/services";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Lock body scroll while mobile menu open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const goToSection = (id) => {
    setMobileOpen(false);
    if (isHome) {
      // IMPORTANT: never use native scrollIntoView / window.scrollTo(smooth)
      // when Lenis is wrapping the page — it desyncs Lenis' internal state
      // from documentElement.scrollTop and causes the next route change to
      // paint blank / black. Always route scroll through Lenis.
      const el = document.getElementById(id);
      if (el && lenis) {
        lenis.scrollTo(el, { duration: 1.1, lock: true });
      } else if (el) {
        el.scrollIntoView({ block: "start" });
      }
    } else {
      navigate(`/#${id}`);
    }
  };

  const goHome = () => {
    if (isHome) {
      if (lenis) {
        lenis.scrollTo(0, { duration: 0.9, lock: true });
      } else {
        window.scrollTo(0, 0);
      }
    } else {
      navigate("/");
    }
  };

  const nav = [
    { id: "approach", label: t.nav.approach },
    { id: "founder", label: t.nav.founder },
    { id: "assessment", label: t.nav.assessment },
  ];

  return (
    <motion.header
      data-testid="site-header"
      initial={false}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 w-full z-50 transition-[background-color,border-color] duration-300 ${
        scrolled || !isHome ? "bg-[#030712]/80 backdrop-blur-xl border-b border-white/10" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <button
          data-testid="nav-home"
          onClick={goHome}
          className="font-display text-lg md:text-xl font-extrabold tracking-tight text-white"
        >
          NexTech<span className="text-[#3B82F6]">.Law</span>
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {nav.map((n) => (
            <button
              key={n.id}
              data-testid={`nav-${n.id}`}
              onClick={() => goToSection(n.id)}
              className="text-sm text-gray-400 hover:text-white transition-colors duration-200"
            >
              {n.label}
            </button>
          ))}
          {/* "CRA" main-nav item removed per Sept 2026 UX decision.
             The /cra page and its content remain fully preserved,
             indexable and reachable via the Insights CRA-Themen-Hub section
             and the footer. */}
          <Link
            to={servicesPath}
            data-testid="nav-services"
            className={`text-sm transition-colors duration-200 ${
              isServicesRoute
                ? "text-white"
                : "text-gray-400 hover:text-white"
            }`}
          >
            {t.nav.services}
          </Link>
          <Link
            to="/insights"
            data-testid="nav-insights"
            className={`text-sm transition-colors duration-200 ${
              location.pathname.startsWith("/insights")
                ? "text-white"
                : "text-gray-400 hover:text-white"
            }`}
          >
            {t.nav.insights}
          </Link>
          <button
            data-testid="nav-contact"
            onClick={() => goToSection("contact")}
            className="text-sm text-gray-400 hover:text-white transition-colors duration-200"
          >
            {t.nav.contact}
          </button>
        </nav>

        <div className="flex items-center gap-3">
          <div className="flex items-center rounded-full border border-white/15 p-0.5 font-mono-tech text-xs">
            {["de", "en"].map((l) => {
              const cls = `px-2.5 py-1 rounded-full transition-colors duration-200 ${
                lang === l ? "bg-white text-black" : "text-gray-400 hover:text-white"
              }`;
              // On language-fixed routes (/services ↔ /en/services) render a
              // real link to the counterpart URL, preserving the hash anchor.
              // On language-fixed routes (/services ↔ /en/services) render a
              // real link to the counterpart URL. The href stays the canonical
              // twin (hash-agnostic → hydration-stable, no duplicate crawlable
              // anchored URLs). A full document navigation (no preventDefault)
              // delivers the prerendered target-language page directly — no
              // wrong-language flash during a client transition. The click
              // handler only rewrites the href to preserve any #anchor for
              // this actual navigation.
              if (servicesTwin && lang !== l) {
                return (
                  <a
                    key={l}
                    href={servicesTwin}
                    hrefLang={l}
                    data-testid={`lang-toggle-${l}`}
                    aria-label={l === "de" ? "Deutsche Version dieser Seite" : "English version of this page"}
                    onClick={(e) => {
                      e.currentTarget.href = servicesTwin + window.location.hash;
                    }}
                    className={cls}
                  >
                    {l.toUpperCase()}
                  </a>
                );
              }
              return (
                <button
                  key={l}
                  data-testid={`lang-toggle-${l}`}
                  onClick={() => setLang(l)}
                  className={cls}
                >
                  {l.toUpperCase()}
                </button>
              );
            })}
          </div>
          <a
            href={BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-cta"
            className="trace-btn hidden sm:inline-flex items-center bg-[#0052FF] hover:bg-[#2563EB] text-white text-sm font-medium px-4 py-2 rounded-sm transition-colors duration-200"
          >
            {t.nav.cta}
          </a>

          {/* Mobile burger */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-sm border border-white/15 text-white hover:bg-white/5 transition-colors"
          >
            {mobileOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {/* Mobile menu panel */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileOpen(false)}
              className="md:hidden fixed inset-0 top-16 bg-black/60 backdrop-blur-sm z-40"
              data-testid="mobile-menu-backdrop"
            />
            <motion.div
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="md:hidden fixed top-16 left-0 right-0 z-40 bg-[#030712] border-b border-white/10 shadow-2xl"
              data-testid="mobile-menu-panel"
            >
              <nav className="px-6 py-6 flex flex-col gap-1">
                {nav.map((n, i) => (
                  <button
                    key={n.id}
                    data-testid={`mobile-nav-${n.id}`}
                    onClick={() => goToSection(n.id)}
                    className="w-full text-left px-4 py-3 rounded-sm text-white text-base font-medium hover:bg-white/5 border-b border-white/5 transition-colors"
                  >
                    <span className="inline-block w-6 font-mono-tech text-[10px] tracking-[0.2em] text-[#3B82F6]">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    {n.label}
                  </button>
                ))}
                <Link
                  to={servicesPath}
                  data-testid="mobile-nav-services"
                  onClick={() => setMobileOpen(false)}
                  className="w-full text-left px-4 py-3 rounded-sm text-white text-base font-medium hover:bg-white/5 border-b border-white/5 transition-colors"
                >
                  <span className="inline-block w-6 font-mono-tech text-[10px] tracking-[0.2em] text-[#3B82F6]">04</span>
                  {t.nav.services}
                </Link>
                <Link
                  to="/insights"
                  data-testid="mobile-nav-insights"
                  onClick={() => setMobileOpen(false)}
                  className="w-full text-left px-4 py-3 rounded-sm text-white text-base font-medium hover:bg-white/5 border-b border-white/5 transition-colors"
                >
                  <span className="inline-block w-6 font-mono-tech text-[10px] tracking-[0.2em] text-[#3B82F6]">05</span>
                  {t.nav.insights}
                </Link>
                <button
                  data-testid="mobile-nav-contact"
                  onClick={() => goToSection("contact")}
                  className="w-full text-left px-4 py-3 rounded-sm text-white text-base font-medium hover:bg-white/5 border-b border-white/5 transition-colors"
                >
                  <span className="inline-block w-6 font-mono-tech text-[10px] tracking-[0.2em] text-[#3B82F6]">06</span>
                  {t.nav.contact}
                </button>
                <div className="mt-3 flex items-center gap-2 px-4">
                  <span className="font-mono-tech text-[10px] tracking-[0.2em] text-gray-400 uppercase">{lang === "de" ? "Sprache" : "Language"}</span>
                  <div className="flex items-center rounded-full border border-white/15 p-0.5 font-mono-tech text-xs">
                    {["de", "en"].map((l) => {
                      const cls = `px-3 py-1.5 rounded-full transition-colors duration-200 min-h-11 inline-flex items-center ${
                        lang === l ? "bg-white text-black" : "text-gray-400 hover:text-white"
                      }`;
                      if (servicesTwin && lang !== l) {
                        return (
                          <a
                            key={l}
                            href={servicesTwin}
                            hrefLang={l}
                            data-testid={`mobile-lang-toggle-${l}`}
                            aria-label={l === "de" ? "Deutsche Version dieser Seite" : "English version of this page"}
                            onClick={(e) => {
                              e.currentTarget.href = servicesTwin + window.location.hash;
                            }}
                            className={cls}
                          >
                            {l.toUpperCase()}
                          </a>
                        );
                      }
                      return (
                        <button
                          key={l}
                          data-testid={`mobile-lang-toggle-${l}`}
                          onClick={() => setLang(l)}
                          className={cls}
                        >
                          {l.toUpperCase()}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <a
                  href={BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mobile-nav-cta"
                  onClick={() => setMobileOpen(false)}
                  className="mt-4 w-full inline-flex items-center justify-center bg-[#0052FF] hover:bg-[#2563EB] text-white font-medium px-4 py-3.5 rounded-sm transition-colors"
                >
                  {t.nav.cta}
                </a>
              </nav>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
